import { contrastRatio, resolveConciergeTheme, isValidThemeHex } from '@dutchie/common/utils/concierge-luminance';
import {
  CONCIERGE_DARK_CANVAS_DERIVATION_CSS,
  CONCIERGE_LIGHT_CANVAS_DERIVATION_CSS,
} from '@dutchie/common/utils/concierge-theme-css';

export { isValidThemeHex };

// Tenant theming overrides for Concierge Mode. Values come from Admin
// (webCustomizationSettingsV2.concierge) and are interpolated into a
// createGlobalStyle, so every hex is strictly re-validated here — a
// malformed value is dropped, never rendered. The light/dark scheme is not
// stored — it derives from the canvas color, classified at render time by
// resolveConciergeTheme's contrast-driven ink selection.
export type ConciergeCustomizationSettings = {
  accentColorHex?: string | null;
  canvasColorHex?: string | null;
};

// The derivation declarations live in @dutchie/common/utils/concierge-theme-css (the
// admin preview interpolates the same strings under its own selectors); this
// file only supplies the validated canvas seed and the production selectors.
function darkCanvasBlock(hex: string): string {
  return `
  .concierge-mode {
    --dcm-concierge-canvas: ${hex};
${CONCIERGE_DARK_CANVAS_DERIVATION_CSS}
  }`;
}

export type ConciergeThemeAttribute = 'light-max-contrast' | 'light';

function lightCanvasBlock(hex: string, attribute: ConciergeThemeAttribute): string {
  return `
  .concierge-mode[data-concierge-theme="${attribute}"] {
    --dcm-concierge-canvas: ${hex};
${CONCIERGE_LIGHT_CANVAS_DERIVATION_CSS}
  }`;
}

// The attribute the wrapper carries, or null for the dark base. Drives both
// the DOM attribute (concierge-providers) and which canvas block is emitted
// below, so the two can never disagree. Dark is the base layer and has no
// attribute value — see the cascade note in concierge-variables.
export function conciergeThemeAttribute(
  settings: ConciergeCustomizationSettings | null | undefined
): ConciergeThemeAttribute | null {
  const canvas = settings?.canvasColorHex;
  if (!isValidThemeHex(canvas)) {
    return null;
  }

  const { scheme, ink } = resolveConciergeTheme(canvas);
  if (scheme === 'DARK') {
    return null;
  }

  return ink === 'MAX_CONTRAST' ? 'light-max-contrast' : 'light';
}

export function buildConciergeOverridesCss(settings: ConciergeCustomizationSettings | null | undefined): string {
  if (!settings) {
    return '';
  }

  const parts: string[] = [];

  if (isValidThemeHex(settings.accentColorHex)) {
    // Only the seed is overridden: every accent-derived token — the accent
    // family, the effect-chip trio, and the strain-dot triad — is declared
    // at .concierge-mode scope in the base blocks, so it recomputes from
    // the cascaded accent value on the same element.
    parts.push(`
  .concierge-mode {
    --dcm-concierge-accent: ${settings.accentColorHex};
  }`);
  }

  if (isValidThemeHex(settings.canvasColorHex)) {
    const attribute = conciergeThemeAttribute(settings);
    parts.push(
      attribute ? lightCanvasBlock(settings.canvasColorHex, attribute) : darkCanvasBlock(settings.canvasColorHex)
    );
  }

  return parts.join('\n');
}

// JS mirror of --dcm-concierge-gold-400 (@dutchie/common/utils/concierge-theme-css);
// a drift test pins the two together. buildConciergeOverridesCss emits nothing
// for an invalid or absent tenant accent and the stylesheet falls back to the
// same primitive. The JS chrome ThemeProvider has no CSS fallback, so it
// computes the same effective value through resolveConciergeAccentHex.
export const CONCIERGE_DEFAULT_ACCENT_HEX = '#d4a843';

const CHROME_FOREGROUND_LIGHT_HEX = '#ffffff';
const CHROME_FOREGROUND_DARK_HEX = '#000000';

/** The effective accent hex: a valid tenant hex, or the stock default. */
export function resolveConciergeAccentHex(settings: ConciergeCustomizationSettings | null | undefined): string {
  const accentHex = settings?.accentColorHex;
  return isValidThemeHex(accentHex) ? accentHex : CONCIERGE_DEFAULT_ACCENT_HEX;
}

/**
 * Contrast-safe ink for chrome surfaces filled with the concierge accent. The
 * accent is never darkened (chrome and remote must agree on it), so the ink
 * flips instead: whichever of pure white/black contrasts higher. That is always
 * >= ~4.58:1, because the better of the two is lowest at relative luminance
 * ~0.179, where both measure ~4.58:1. Ties go to white.
 */
export function resolveConciergeChromeForeground(accentHex: string): string {
  const lightContrast = contrastRatio(accentHex, CHROME_FOREGROUND_LIGHT_HEX);
  const darkContrast = contrastRatio(accentHex, CHROME_FOREGROUND_DARK_HEX);
  return lightContrast >= darkContrast ? CHROME_FOREGROUND_LIGHT_HEX : CHROME_FOREGROUND_DARK_HEX;
}
